import { LABELS } from '@mm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/inputs';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../auth/AuthProvider';
import { sources } from './sources';
import {
  Alert,
  Card,
  DetailList,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../../components/ui/display';
import type { App } from '../../types/models';
import { VerificationBadge } from './configs';
import { formatDate, formatDateTime } from '../../utils/format';
import { AppAccountsCard } from '../assignments/AppAccountsCard';
import { PerformanceCard } from '../reports/PerformanceCard';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { resourceKey } from './MasterPage';

function useRecord<T>(resource: string, id: string | undefined) {
  return useQuery({
    queryKey: [...resourceKey(resource), 'detail', id],
    queryFn: () => api.get<T>(`/${resource}/${id}`),
    enabled: !!id,
  });
}

function LoadState({ error }: { error: unknown }) {
  if (!error) return <Spinner />;
  return <Alert tone="error">{error instanceof ApiError ? error.message : 'Failed to load'}</Alert>;
}

const link = (to: string, label: string) => (
  <Link to={to} className="text-brand-700 hover:underline">
    {label}
  </Link>
);

/** A link that opens in a new tab. */
function OpenLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="break-all text-brand-700 hover:underline"
    >
      {href}
    </a>
  );
}

/** Read-only details from the Google Play listing, refreshed on demand. */
function PlayListingCard({ app }: { app: App }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api.post<App>(`/apps/${app.id}/play-store-sync`),
    onSuccess: () => {
      toast.success('Updated from Google Play');
      void queryClient.invalidateQueries({ queryKey: resourceKey('apps') });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Google Play could not be read'),
  });
  const aboutDeveloper = [app.developerLegalName, app.developerAddress, app.developerPhone]
    .filter(Boolean)
    .join('\n');
  return (
    <Card
      title="Google Play listing"
      description={
        app.storeSyncedAt
          ? `Read from Google Play on ${formatDateTime(app.storeSyncedAt)}`
          : 'Not read from Google Play yet'
      }
      actions={
        app.playStoreUrl &&
        can('masters.write') && (
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="size-4" />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            Refresh from Play Store
          </Button>
        )
      }
    >
      {!app.playStoreUrl ? (
        <p className="text-sm text-slate-500">
          Add the Google Play link (Edit app) to read these details from the listing.
        </p>
      ) : (
        <DetailList
          items={[
            {
              label: 'Google Play',
              value: (
                <a
                  href={app.playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                >
                  Open listing <ExternalLink className="size-3.5" />
                </a>
              ),
            },
            {
              label: 'Support email',
              value: app.developerEmail ? (
                <a href={`mailto:${app.developerEmail}`} className="text-brand-700 hover:underline">
                  {app.developerEmail}
                </a>
              ) : null,
            },
            { label: 'Released on', value: app.releasedOn ? formatDate(app.releasedOn) : null },
            {
              label: 'Last updated on Play',
              value: app.storeUpdatedOn ? formatDate(app.storeUpdatedOn) : null,
            },
            {
              label: 'Website',
              value: app.developerWebsite ? <OpenLink href={app.developerWebsite} /> : null,
            },
            {
              label: 'Privacy policy',
              value: app.privacyPolicyUrl ? <OpenLink href={app.privacyPolicyUrl} /> : null,
            },
            {
              label: 'Developer email',
              value: app.developerContactEmail ? (
                <a
                  href={`mailto:${app.developerContactEmail}`}
                  className="text-brand-700 hover:underline"
                >
                  {app.developerContactEmail}
                </a>
              ) : null,
            },
            {
              label: 'About the developer',
              value: aboutDeveloper ? (
                <span className="whitespace-pre-line">{aboutDeveloper}</span>
              ) : null,
            },
          ]}
        />
      )}
    </Card>
  );
}

/** The Google Play developer account that publishes the app; changed here (administrators). */
function DeveloperAccountCard({ app }: { app: App }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [accountId, setAccountId] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.put(`/apps/${app.id}`, { playDeveloperAccountId: accountId, updatedAt: app.updatedAt }),
    onSuccess: () => {
      toast.success('Developer account saved');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: resourceKey('apps') });
      void queryClient.invalidateQueries({ queryKey: resourceKey('play-developer-accounts') });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Saving failed'),
  });
  const dev = app.playDeveloperAccount;
  return (
    <Card
      title="Google Play developer account"
      actions={
        can('masters.write') &&
        !editing && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAccountId(app.playDeveloperAccountId ?? '');
              setEditing(true);
            }}
          >
            {dev ? 'Change' : 'Assign developer account'}
          </Button>
        )
      }
    >
      {editing ? (
        <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Developer account" htmlFor="dev-account">
            <SearchableSelect
              inputId="dev-account"
              value={accountId}
              onChange={(v) => setAccountId(v ?? '')}
              remote={sources.playDeveloperAccount()}
              clearable
            />
          </Field>
          <div className="flex gap-2 sm:col-span-2">
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : dev ? (
        <DetailList
          items={[
            {
              label: 'Developer account',
              value: link(`/play-developer-accounts/${dev.id}`, dev.name),
            },
            { label: 'Developer ID', value: dev.developerId },
            { label: 'Login email', value: dev.loginEmail },
            { label: 'Access email', value: dev.accessEmail },
            {
              label: 'Verification',
              value: <VerificationBadge status={dev.verificationStatus ?? null} />,
            },
          ]}
        />
      ) : (
        <p className="text-sm text-slate-500">No developer account linked yet.</p>
      )}
    </Card>
  );
}

/** The app's Firebase account and project; changed here (administrators). */
function FirebaseCard({ app }: { app: App }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [projectId, setProjectId] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.put(`/apps/${app.id}`, {
        firebaseAccountId: accountId,
        firebaseProjectId: projectId,
        updatedAt: app.updatedAt,
      }),
    onSuccess: () => {
      toast.success('Firebase account saved');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: resourceKey('apps') });
      void queryClient.invalidateQueries({ queryKey: resourceKey('firebase-accounts') });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Saving failed'),
  });
  const fb = app.firebaseAccount;
  return (
    <Card
      title="Firebase"
      actions={
        can('masters.write') &&
        !editing && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAccountId(app.firebaseAccountId ?? '');
              setProjectId(app.firebaseProjectId ?? '');
              setEditing(true);
            }}
          >
            {fb ? 'Change' : 'Assign Firebase account'}
          </Button>
        )
      }
    >
      {editing ? (
        <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Firebase account" htmlFor="fb-account">
            <SearchableSelect
              inputId="fb-account"
              value={accountId}
              onChange={(v) => setAccountId(v ?? '')}
              remote={sources.firebaseAccount()}
              clearable
            />
          </Field>
          <Field label="Firebase project ID" htmlFor="fb-project" help="e.g. my-app-12345">
            <Input
              id="fb-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            />
          </Field>
          <div className="flex gap-2 sm:col-span-2">
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : fb ? (
        <DetailList
          items={[
            { label: 'Firebase account', value: link('/firebase-accounts', fb.name) },
            { label: 'Project ID', value: app.firebaseProjectId },
            { label: 'Login email', value: fb.loginEmail },
            { label: 'Access email', value: fb.accessEmail },
          ]}
        />
      ) : (
        <p className="text-sm text-slate-500">No Firebase account linked yet.</p>
      )}
    </Card>
  );
}

export function AppDetailPage() {
  const { id } = useParams();
  const { data: app, error } = useRecord<App>('apps', id);
  if (!app) return <LoadState error={error} />;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={link('/apps', 'Apps')}
        title={
          <span className="flex items-center gap-3">
            <AppIcon src={app.iconUrl} name={app.name} size={44} />
            {app.name}
          </span>
        }
        description={app.developerName ? `by ${app.developerName}` : (app.packageName ?? undefined)}
        actions={<StatusBadge status={app.status} />}
      />
      <Card title="App information">
        <DetailList
          items={[
            { label: 'Developer account', value: app.developerName },
            { label: 'Platform', value: LABELS.appPlatform[app.platform] },
            { label: 'Package name', value: app.packageName },
            { label: 'Store category', value: app.storeCategory },
            { label: 'Developer account access email', value: app.developerAccessEmail },
            { label: 'Created', value: formatDateTime(app.createdAt) },
            { label: 'Description', value: app.description },
            { label: 'Notes', value: app.notes },
          ]}
        />
      </Card>
      <PlayListingCard app={app} />
      <DeveloperAccountCard app={app} />
      <FirebaseCard app={app} />
      <AppAccountsCard
        appId={app.id}
        appName={app.name}
        appActive={app.status === 'ACTIVE'}
        kind="advertising"
      />
      <AppAccountsCard
        appId={app.id}
        appName={app.name}
        appActive={app.status === 'ACTIVE'}
        kind="revenue"
      />
      <AppPerformance appId={app.id} />
    </div>
  );
}

export { AdvertisingAccountDetailPage } from './AdvertisingAccountDetail';

export { RevenueAccountDetailPage } from './RevenueAccountDetail';

export { PlayDeveloperAccountDetailPage } from './PlayDeveloperAccountDetail';

/** App detail: profit for a chosen range (default this month), after month-end actuals. */
function AppPerformance({ appId }: { appId: string }) {
  const [range, setRange] = useDateFilter('thisMonth');
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Performance</h2>
        <DateRangeFilter value={range} onChange={setRange} />
      </div>
      <PerformanceCard
        startDate={range.startDate}
        endDate={range.endDate}
        appId={appId}
        title="Revenue, expenses and profit"
      />
    </section>
  );
}
